/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { isCancel } from 'axios';
import { getStore } from 'store/accessor';
import { authActions } from '../auth/slice';
import TIME_OUTS from '../constants/time-outs';
import httpClient from './axios';

export class ResponseError extends Error {
  constructor(error) {
    const response = error?.response;
    const message = response?.data ?? error?.message ?? 'Request failed';
    super(message);
    this.name = 'ResponseError';
    // Error() stringifies non-string payloads; keep the original value.
    this.message = message;
    this.status = response?.status ?? null;
    this.code = error?.code ?? null;
    this.data = response?.data ?? null;
    this.isCanceled = isCancel(error);
    this.isTimeout = ['ECONNABORTED', 'ETIMEDOUT'].includes(error?.code);
    this.isNetworkError = !response && !this.isCanceled && !this.isTimeout;
  }
}
/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object} The parsed JSON from the request
 */
const parseJSON = (response) => {
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  return response.data;
};
/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
const errorHandling = async (error) => {
  const err = new ResponseError(error);
  if (err.status === 401 && err.data === 'TOKEN_REVOCATION') {
    getStore()?.dispatch(authActions.setTokenRevocationError(true));
  }
  throw err;
};
/**
 * Replaces `:param` tokens in a request path with values from the payload.
 * Tokens before the first `?` are URL-encoded path parameters (required);
 * tokens after `?` are raw query segments (optional, `''` when missing).
 * Consumed keys are removed from the body payload.
 *
 * @param  {string} path    The request path, e.g. '/items/:id?:query'
 * @param  {object} payload The request payload containing parameter values
 *
 * @return {object}         { path, body }
 */
export const buildPath = (path, payload) => {
  const body = { ...payload };
  const queryIndex = path.indexOf('?');
  const pathPart = queryIndex === -1 ? path : path.slice(0, queryIndex);
  const queryPart = queryIndex === -1 ? undefined : path.slice(queryIndex + 1);
  //
  const resolvedPath = pathPart.replace(/:([A-Za-z0-9_]+)/g, (match, name) => {
    const value = body[name];
    if (value === undefined || value === null) {
      throw new Error(`Missing path parameter ":${name}" for ${path}`);
    }
    delete body[name];
    return encodeURIComponent(String(value));
  });
  //
  const resolvedQuery = queryPart?.replace(/:([A-Za-z0-9_]+)/g, (match, name) => {
    const value = body[name] ?? '';
    delete body[name];
    return value;
  });
  //
  return {
    path: queryPart === undefined ? resolvedPath : `${resolvedPath}?${resolvedQuery}`,
    body,
  };
};
/**
 * Sends an API request through the configured Axios client and returns the response data.
 *
 * @param  {object}  _metadata          Endpoint metadata: `path` (may contain `:param` tokens), `method`, optional `timeout` in ms
 * @param  {object}  [data]             Payload; values for `:param` tokens are taken from it and the rest is sent as the body
 * @param  {boolean} [isSecure=true]    Adds the Authorization header, which the client interceptor fills with the access token
 * @param  {boolean} [multipart=false]  Sends `data.file` as multipart form data
 * @param  {object}  [options]          Request options
 * @param  {AbortSignal} [options.signal] Signal used to cancel the request
 * @return {Promise<object|null>}       Response data, or null for 204/205 responses; rejects with ResponseError
 */
const request = async (_metadata, data, isSecure = true, multipart = false, { signal } = {}) => {
  const metadata = { ..._metadata };
  const { path, body: payload } = buildPath(metadata.path, data);
  metadata.path = path;
  //
  let requestBody = JSON.stringify(payload);
  if (multipart) {
    const formData = new FormData();
    formData.append('file', payload.file);
    requestBody = formData;
  }
  const requestOptions = {
    method: metadata.method,
    // mode: 'cors', // no-cors, *cors, same-origin
    cache: 'no-cache',
    url: metadata.path,
    signal,
    timeout: metadata.timeout ?? TIME_OUTS.REQUEST_TIMEOUT_MS,
    // credentials: 'include', // include, *same-origin, omit
    headers: {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS',
      ...(isSecure && {
        Authorization: '',
      }),
      ...(multipart && {
        enctype: 'multipart/form-data',
      }),
    },
    redirect: 'follow', // manual, *follow, error
    referrer: 'no-referrer', // no-referrer, *client
    ...(['POST', 'PUT', 'PATCH'].includes(metadata.method) && {
      data: requestBody,
    }),
  };
  //
  try {
    const response = await httpClient(requestOptions);
    return parseJSON(response);
  } catch (error) {
    return errorHandling(error);
  }
};
export default request;
